import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzTableModule } from 'ng-zorro-antd/table';
import { v4 as uuidv4 } from 'uuid';
import { StarRankerComponent } from '../star-ranker/star-ranker.component';

export interface TableColumn {
  display_name: string;
  mapping_key: string;
  isExpandable?: boolean;
  isVisible: boolean;
  isSelected?: boolean;
  headerStyles?: { [key: string]: string };
}

@Component({
  selector: 'app-expand-table-component',
  standalone: true,
  imports: [CommonModule, NzTableModule, StarRankerComponent, FormsModule],
  templateUrl: './expand-table-component.component.html',
  styleUrls: ['./expand-table-component.component.scss']
})
export class ExpandTableComponentComponent implements OnInit {
  @Input() rows: any[] = [];
  @Input() columns: TableColumn[] = [];
  @Input() showPagination = true;
  @Output() selectionChange = new EventEmitter<any>();
  @Output() openReportEvent = new EventEmitter<any>();

  visibleColumns: TableColumn[] = [];
  visibleColumnKeys: Set<string> = new Set();
  hiddenColumnKeys: string[] = [];
  allColumnKeys: string[] = [];

  pageIndex = 1;
  pageSize = 10;
  expandSet = new Set<string>();
  setOfCheckedId = new Set<string>();
  checked = false;
  indeterminate = false;
  hasCheckboxColumn = false;

  ngOnInit(): void {
    // Assign a unique ID to each row to manage expand state correctly
    this.rows = this.rows.map(row => ({ ...row, _uuid: uuidv4(), isSelected: false }));

    // Check if a checkbox column is provided
    this.hasCheckboxColumn = this.columns.some(c => c.mapping_key === '_checkbox');

    this.visibleColumns = this.columns.filter(c => c.isVisible);
    this.visibleColumnKeys = new Set(this.visibleColumns.map(c => c.mapping_key));
    this.hiddenColumnKeys = this.columns
      .filter(c => !c.isVisible)
      .map(c => c.mapping_key);
    this.allColumnKeys = this.columns.map(c => c.mapping_key);
  }

  onAllChecked(checked: boolean): void {
    this.rows.forEach(({ _uuid }) => {
      this.updateCheckedSet(_uuid, checked);
      const row = this.rows.find(r => r._uuid === _uuid);
      if (row) {
        row.isSelected = checked;
      }
    });
    this.refreshCheckedStatus();
    this.emitSelection();
  }

  onItemChecked(id: string, checked: boolean): void {
    this.updateCheckedSet(id, checked);
    // Update the isSelected property on the row
    const row = this.rows.find(r => r._uuid === id);
    if (row) {
      row.isSelected = checked;
    }
    this.refreshCheckedStatus();
    this.emitSelection();
  }

  updateCheckedSet(id: string, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
  }

  refreshCheckedStatus(): void {
    this.checked = this.rows.every(({ _uuid }) => this.setOfCheckedId.has(_uuid));
    this.indeterminate = !this.checked && this.rows.some(({ _uuid }) => this.setOfCheckedId.has(_uuid));
  }

  emitSelection(): void {
    const selectedRows = this.rows.filter(row => this.setOfCheckedId.has(row._uuid));
    this.selectionChange.emit(
      {
        type: 'row_select',
        data: selectedRows
      }
    );
  }

  onExpandChange(id: string, checked: boolean): void {
    if (checked) {
      this.expandSet.add(id);
    } else {
      this.expandSet.delete(id);
    }
  }

  // Helper to get all keys of an object for the template
  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  // Helper to check if a value is an array
  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  // Helper to check if a string should be treated as a list
  isStringList(value: any): boolean {
    return typeof value === 'string' && (value.includes('#') || value.includes('*'));
  }

  // Helper to split the string into an array for the template
  splitStringList(value: string): string[] {
    return value.split(/[#*]/).map(item => item.trim()).filter(item => item.length > 0);
  }

  // Returns a display-friendly title from a mapping key
  getSectionTitle(key: string): string {
    const column = this.columns.find(c => c.mapping_key === key);
    return column ? column.display_name : key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  }

  // Placeholder for report opening logic
  openReport(data: string): void {
    console.log('Opening report for:', data);
    this.openReportEvent.emit(data);
  }
}